import { client } from '../client';
import { ApiError } from '../errors';
import type { UserId } from '../ids';
import { parseShimResponse } from '../shim-parse';
import { etagFor, rememberEtag } from '../etag-store';
import {
  NotificationPreferenceHistorySchema,
  NotificationPreferencesSchema,
} from './notification-preferences.schema';

/*
 * Notification-preferences transport (F02 R25/R26/R35 — transport in lib,
 * consumption in features). Says WHAT endpoints exist for a client user's own
 * notification choices; not how the UI consumes them.
 *
 * These are the domain BFF's preference routes (F02 design §4.3, TPA-owned):
 *   GET  /users/:id/notification-preferences   the user's four choices
 *   PUT  /users/:id/notification-preferences   replace the user's choices
 * They are NOT in `openapi.json` yet (the preference table is TPA-owned but the
 * BFF route is not generated), so — exactly as `users.ts` does (ADR 0009) — the
 * wire shapes are hand-authored here and go through the ONE network door
 * (`client`) via a single, boundary-confined untyped shim, removed when these
 * routes are added to `openapi.json`.
 *
 * Nothing here decides a rule: the server is the authority (conventions,
 * "Identity"; product rule — the server backstops permissions). The four keys
 * are a GOVERNED REGISTRY (AC-09826b9a), the write is ALL-OR-NOTHING on an
 * unknown key (AC-8ddebd3b) and DEFAULT-ON is applied on read (AC-f00ecffd) —
 * all enforced by the domain service. The frontend's job is the courtesy half:
 * show the four choices, reflect default-on, and surface the server's whole-
 * submission refusal (naming the unrecognized keys) where the person works.
 *
 * No PII: the four keys are GOVERNED IDS, not names/emails; the request targets
 * the account by id (AGENTS.md — ids only, never PII in URLs/telemetry).
 */

/*
 * The four governed notification-preference keys (AC-09826b9a), mirroring the
 * domain service's governed registry and the schema's seeded keys. The key
 * space is fixed: a submission naming anything else is refused whole by the
 * server (AC-8ddebd3b). These are opaque governed ids, never PII.
 */
export const NOTIFICATION_PREFERENCE_KEYS = [
  'payment_due_reminder', // the billing notification — ON BY DEFAULT (AC-e0a73de2)
  'auto_debit_approaching',
  'auto_debit_posted',
  'auto_debit_returned_nsf',
] as const;

/** One governed notification-preference key. */
export type NotificationPreferenceKey =
  (typeof NOTIFICATION_PREFERENCE_KEYS)[number];

/**
 * A user's notification choices as the server returns them (GET): every
 * governed key with its effective answer, DEFAULT-ON already applied by the
 * server for any key the person never answered (AC-f00ecffd) — so the billing
 * notification reads `true` for a new client user (AC-e0a73de2) unless it was
 * turned off (AC-a538e368). The frontend renders these as-is; it never computes
 * the default itself.
 */
export type NotificationPreferences = Record<
  NotificationPreferenceKey,
  boolean
>;

/**
 * The body of `PUT /users/:id/notification-preferences`: the choices to store,
 * one answer per key (AC-dd4c1e06). A submission naming anything outside the
 * four governed keys is refused WHOLE by the server, changing nothing
 * (AC-8ddebd3b), and the response names the unrecognized keys (AC-c7c5f9af).
 */
export interface UpdateNotificationPreferencesInput {
  choices: NotificationPreferences;
}

/**
 * One entry in a user's notification-preference CHANGE HISTORY (F02 R28/R29):
 * for a single changed choice, what it was BEFORE and AFTER (AC-b1d8996b) and
 * WHO changed it (AC-bf8668ea / AC-58e727f7). The server reads this from the
 * TPA-owned change record (the preference table is TPA's; Identity's audit does
 * not see it), so the before/after and the changer are TPA's own.
 *
 * ATTRIBUTION NAMES THE PERSON, NEVER THE ORGANIZATION (feature design §3, R28).
 * `changedByName` is the display NAME of the person who made the change — never
 * the organization — or null when the server could not establish who acted
 * (R28's F69 edge: the entry names no person and still never the organization).
 * `self` says whether the change was the user's OWN (AC-58e727f7) or someone
 * else's (AC-bf8668ea), so the portal can show "you changed it" versus
 * "<name> changed it". The changer id is an OPAQUE id for keys/requests, never
 * shown — the NAME is the display, the id is for correlation (no PII in URLs).
 */
export interface NotificationPreferenceChange {
  /** The governed key that changed. */
  key: NotificationPreferenceKey;
  /**
   * The answer in force BEFORE this change (AC-b1d8996b), or null when the
   * person had never answered the key before (the default was in force) —
   * mirroring the nullable `enabled_before` column. The history shows what the
   * choice WAS before; a never-answered key had no stored answer.
   */
  enabledBefore: boolean | null;
  /** The answer this change set (AC-b1d8996b). */
  enabledAfter: boolean;
  /**
   * Whether the change was the user's OWN (AC-58e727f7) rather than made on
   * their behalf by someone else (AC-bf8668ea). The portal shows a self-change
   * as the user's own and an on-behalf change as the named person's.
   */
  self: boolean;
  /**
   * The display NAME of the person who made the change — the PERSON, NEVER the
   * organization (R28) — or null when who acted could not be established. For a
   * self-change this is the user themselves; for an on-behalf change it is the
   * administrator who acted (e.g. "Kelly changed it, and who").
   */
  changedByName: string | null;
  /** When the change was made, as an ISO-8601 instant (formatted in the UI). */
  changedAt: string;
}

/**
 * A user's notification-preference change history (newest first), as the server
 * returns it from `GET /users/:id/notification-preferences/history` (F02
 * R28/R29). Each entry carries the before/after and who changed it. Read from
 * the TPA-owned change record.
 */
export type NotificationPreferenceHistory = NotificationPreferenceChange[];

/*
 * The preference routes are the domain BFF's TPA-owned routes, not in
 * `openapi.json` (ADR 0009), so the typed `client` has no path entry. They
 * still go through the ONE network door — `client` — honouring "no fetch
 * outside lib/api"; the single cast here is the one boundary allowed to know
 * these untyped routes, removed when they are added to `openapi.json`.
 * errorMiddleware turns a 4xx into an `ApiError` the same way as every typed
 * route, so the unknown-key refusal surfaces as an `ApiError` the screen reads.
 */
type UntypedResult = { data?: unknown; response?: Response };
type UntypedClient = {
  GET: (path: string, init?: Record<string, unknown>) => Promise<UntypedResult>;
  PUT: (path: string, init?: Record<string, unknown>) => Promise<UntypedResult>;
};
const untypedClient = client as unknown as UntypedClient;

/* If-Match for an update from the resource's last-GET ETag (finding 8). */
function ifMatch(path: string): Record<string, string> {
  const etag = etagFor(path);
  return etag ? { 'If-Match': etag } : {};
}

export const notificationPreferencesApi = {
  /**
   * The user's four notification choices, DEFAULT-ON already applied by the
   * server (AC-f00ecffd). The request targets the account by id — no PII in the
   * URL. Held per person (AC-3d6561d1).
   */
  async get(userId: UserId): Promise<NotificationPreferences> {
    const path = `/users/${encodeURIComponent(userId)}/notification-preferences`;
    const { data, response } = await untypedClient.GET(path);
    // Remember the preferences' ETag so a save sends If-Match (finding 8); a
    // concurrent change (self or on-behalf) makes it stale → 412 collision.
    rememberEtag(path, response?.headers.get('ETag') ?? null);
    return parseShimResponse(NotificationPreferencesSchema, data, path);
  },

  /**
   * Replace the user's notification choices (PUT), one answer per key
   * (AC-dd4c1e06). A submission naming an unknown key is refused WHOLE by the
   * server with a 4xx carrying the unrecognized names (AC-8ddebd3b,
   * AC-c7c5f9af); it arrives here as an `ApiError` the screen surfaces where the
   * person works. On success the server returns the stored choices.
   */
  async update(
    userId: UserId,
    body: UpdateNotificationPreferencesInput,
  ): Promise<NotificationPreferences> {
    const path = `/users/${encodeURIComponent(userId)}/notification-preferences`;
    const { data } = await untypedClient.PUT(path, {
      body,
      headers: ifMatch(path),
    });
    return parseShimResponse(NotificationPreferencesSchema, data, path);
  },

  /**
   * Change a user's notification choices ON THAT USER'S BEHALF (F02 R27,
   * AC-7aaa025f). The ADMINISTRATOR surface: the request targets the user by id
   * within their own organization, and the server SCOPES it — an administrator
   * may change a user's choices only within that user's OWN organization
   * (AC-7aaa025f, Identity's scope). An out-of-scope target is refused with the
   * byte-identical not-found (404), exactly like every scoped read; the frontend
   * never widens the scope.
   *
   * The server attributes the change to the ACTOR (the administrator making the
   * request, established server-side from the session — NOT from anything the
   * browser sends), so the change record names the person who acted (AC-bf8668ea)
   * and never the target or the organization. The frontend sends only the target
   * id and the choices; WHO acted is the server's, from `/me`, never the
   * browser's (service-context: who the caller is comes from the server). Same
   * all-or-nothing-on-unknown-key guarantee as the self-service write
   * (AC-8ddebd3b); the refusal surfaces as an `ApiError`. The target is addressed
   * by id — no PII in the URL.
   */
  async updateOnBehalf(
    userId: UserId,
    body: UpdateNotificationPreferencesInput,
  ): Promise<NotificationPreferences> {
    const path = `/users/${encodeURIComponent(userId)}/notification-preferences`;
    const { data } = await untypedClient.PUT(path, {
      body,
      headers: ifMatch(path),
    });
    return parseShimResponse(NotificationPreferencesSchema, data, path);
  },

  /**
   * A user's notification-preference CHANGE HISTORY (F02 R28/R29): each changed
   * choice's before/after (AC-b1d8996b) and who changed it, naming the PERSON
   * never the organization (AC-bf8668ea / AC-58e727f7). Read from the TPA-owned
   * change record. The target is addressed by id — no PII in the URL; the
   * changer's NAME comes back in the body for display, never in the URL.
   */
  async getHistory(userId: UserId): Promise<NotificationPreferenceHistory> {
    const path = `/users/${encodeURIComponent(userId)}/notification-preferences/history`;
    const { data } = await untypedClient.GET(path);
    return parseShimResponse(NotificationPreferenceHistorySchema, data, path);
  },
};

/*
 * The `code` the preference PUT 400 carries when the submission named one or
 * more keys outside the governed four — the one string that distinguishes a
 * whole-submission refusal from any other 4xx (AC-8ddebd3b).
 */
const UNKNOWN_KEY_CODE = 'unknown_notification_key';

/**
 * Recover the UNRECOGNIZED key names from a failed `update`, or null if the
 * failure is not an unknown-key refusal (AC-c7c5f9af). This is the single
 * boundary that knows the refusal's extension shape: it reads the raw Problem
 * body off the `ApiError`, confirms the unknown-key `code`, and narrows the
 * `unrecognizedKeys` to a string list for the screen to name. Any other failure
 * — a validation error, a transient 5xx — returns null, so only a genuine
 * unknown-key refusal names keys; the whole-submission-refused guarantee
 * (nothing written) is the server's (AC-8ddebd3b).
 */
export function unrecognizedKeysFromError(error: unknown): string[] | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status !== 400 || error.code !== UNKNOWN_KEY_CODE) return null;
  const body = error.body as { unrecognizedKeys?: unknown } | undefined;
  const keys = body?.unrecognizedKeys;
  if (!Array.isArray(keys) || keys.some((k) => typeof k !== 'string')) {
    return null;
  }
  return keys as string[];
}
